
import { IPlanificationPhase } from '@/entity/planification/planification-phase';
import { IPlanificationPhaseProfile } from '@/entity/planification/planification-phase-profile';
import { IProject } from '@/entity/project/project';
import { IStudio } from '@/entity/project/studio';
import { IEmployee } from '@/entity/shared/employee';
import { IEmployeeRole } from '@/entity/shared/referential';
import { vxm } from '@/store';
import { planificationApi } from '@/wapi/planification-api';
import CalendarDayPicker from '@c/shared/calendar-day-picker.vue';
import CurrencyInput from '@c/shared/currency-input.vue';
import InputElementMgt from '@c/shared/input-element-mgt.vue';
import { getEmployeCost } from '@t/cost-utils';
import { NU } from '@t/type';
import { isAfter, isEqual } from 'date-fns';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { PlanificationValidation } from '../interface/project-planification';

@Component({
    components: {
        InputElementMgt,
        CurrencyInput,
        CalendarDayPicker
    }
})
export default class ProfileInformation extends Vue {
    @Prop({ required: true }) selectedPlanificationPhaseToUpdate!: IPlanificationPhase;
    @Prop({ required: true }) selectedProfile!: IPlanificationPhaseProfile;
    @Prop({ required: true }) profileIndex!: number;
    @Prop({ required: true }) studios!: NU<IStudio[]>;
    @Prop({ required: true }) showValidation!: boolean;
    @Prop({ required: true }) validationObject!: Array<PlanificationValidation>;
    @Prop({ required: true }) isAddProfileBouttonClicked!: boolean;
    @Prop({ required: true }) setOpeningAndClosingDateForPhase!: (phase: IPlanificationPhase) => void;

    get forcedKeyUpdate() {
        return `${this.selectedPlanificationPhaseToUpdate.id}_${this.profileIndex}_${this.selectedProfile.employeeId}`;
    }

    get isDisplayRoleLabel() {
        return (
            !this.selectedProfile.employeeRoleLabel &&
            this.selectedProfile.employeeRole &&
            this.selectedProfile.employeeId &&
            this.selectedProfile.totalRate
        );
    }

    get profilClassName() {
        return {
            'error-planif':
                this.showValidation === true &&
                this.validationObject.length > this.profileIndex &&
                this.validationObject[this.profileIndex].profil === true
        };
    }

    get getDefaultIds() {
        return this.selectedProfile.employeeId && this.selectedProfile.employeeId.length > 0
            ? this.selectedProfile.employeeId
            : '';
    }

    get dayCount() {
        return this.selectedProfile.planificationPhaseProfileWorkDays
            ? this.selectedProfile.planificationPhaseProfileWorkDays.length / 2
            : 0;
    }

    get isCP(): boolean {
        return vxm.user.isCP;
    }

    get isReadOnly(): boolean {
        return !vxm.user.isStudioManager && !vxm.user.isAdmin && !vxm.user.isCP;
    }

    get groupPickerId() {
        if (process.env.NODE_ENV === 'production') {
            return 'e59b07b5-a6f9-4040-b817-bad6fe2f2f1b';
        }
        return null;
    }

    get employeeRoles(): NU<IEmployeeRole[]> {
        return vxm.referential.employeeRoles;
    }

    get selectedProject(): NU<IProject> {
        return vxm.project.dropdownProject;
    }

    deleteProfileModal(): void {
        this.$emit('onDelete');
    }

    async selectedUserEvent(event: CustomEvent) {
        const u =
            event && event.detail.length
                ? await planificationApi.getEmployeeHistorization(event.detail[0].id as string)
                : null;
        this.selectedProfile.employee = typeof u?.datas === 'object' ? (u?.datas as IEmployee) : null!;
        const lastEmployeeRole = u?.datas?.histos?.slice(-1)[0];
        this.selectedProfile.employeeName = '';
        this.selectedProfile.employeeRoleId = lastEmployeeRole?.employeeRoleId;
        this.selectedProfile.employeeRoleLabel = lastEmployeeRole?.employeeRole ?? '';
        this.$emit('selectedUserEvent', {
            event: event,
            index: this.profileIndex,
            profile: this.selectedProfile
        });
    }

    private assignEmployeeRole(profile: IPlanificationPhaseProfile, index: number) {
        this.setProfileTotalRate(profile, index);
        if (
            this.showValidation === true &&
            this.validationObject.length > index &&
            this.validationObject[index].profil === true
        ) {
            this.validationObject[index].profil = false;
        }
    }

    studioInput(v?: number) {
        if (
            v != null &&
            this.showValidation === true &&
            this.validationObject.length > this.profileIndex &&
            this.validationObject[this.profileIndex].studio === true
        ) {
            this.validationObject[this.profileIndex].studio = false;
        }
    }

    setProfileTotalRate(profile, index: number = -1): void {
        const projectEntityLabel = this.selectedProject?.entity.label;
        if (profile.employee != null) {
            if (this.employeeRoles != null) {
                const employeeRoleList = profile.employee?.histos;
                if (employeeRoleList != null) {
                    let totalRate = 0;
                    employeeRoleList?.forEach((employeeRole) => {
                        const eRole = this.employeeRoles?.find((r) => r.id === employeeRole?.employeeRoleId);
                        const eRoleRate = getEmployeCost(eRole, projectEntityLabel!);
                        const planificationPhaseProfileWorkDaysByRole =
                            profile.planificationPhaseProfileWorkDays.filter((day) => {
                                return (
                                    isAfter(
                                        new Date(day.date).setHours(0, 0, 0, 0),
                                        new Date(employeeRole.startDate).setHours(0, 0, 0, 0)
                                    ) ||
                                    isEqual(
                                        new Date(day.date).setHours(0, 0, 0, 0),
                                        new Date(employeeRole.startDate).setHours(0, 0, 0, 0)
                                    )
                                );
                            });
                        totalRate = 0;
                        totalRate += eRoleRate * (planificationPhaseProfileWorkDaysByRole?.length ?? 0);
                    });
                    profile.totalRate = totalRate;
                }
            }
        } else {
            const eRole = this.employeeRoles?.find(
                (x) => x.id === profile.employeeRole?.id || x.label === profile.profileRole
            );
            profile.employeeRoleId = profile.employeeRole?.id;
            profile.totalRate =
                profile.planificationPhaseProfileWorkDays.length * getEmployeCost(eRole!, projectEntityLabel!);
        }
        const profileToUpdate = this.selectedPlanificationPhaseToUpdate.planificationPhaseProfiles[index];
        if (profileToUpdate) {
            profileToUpdate.totalRate = profile.totalRate;
        }
    }

    private canEditOrDelete(): boolean {
        /* à retirer todo ! */
        return vxm.user.isAdmin || vxm.user.isStudioManager || true;
    }
}
